import React from 'react';
import PropTypes from 'prop-types';
import CheckBox from '../form-controls/CheckBox';
import StorefrontPersona from './StorefrontPersona';
import WarrantyAccess from './WarrantyAccess';
import Branches from './Branches';
import Clone from './Clone';
import Authorize from '../Authorize';
import Loading from '../Loading';
import UserHelper from '../../helpers/UserHelper';
import MUIModal from '../shared/MUIModal';
import MUIModalButtonStyles from '../shared/MUIStyles/MUIModalButtonStyles';
import { Button } from '@mui/material';

const UserRoles = ({
    user,
    persona,
    storefront,
    modals,
    handlers,
    localized,
    currentUser,
    branches
}) => {
    return (
        <div className="sub flex">
            {/* Storefront & Persona */}
            <div>
                <div className="role button-wrapper">
                    <div>
                        <div className="label">{localized.Storefront}</div>
                        <span className="current" style={{ marginLeft: '4px' }}>{storefront.displayName}</span>
                    </div>
                    <div>
                        <div className="label">{localized.Persona}</div>
                        <span className="current" style={{ marginLeft: '4px' }}>{persona.label}</span>
                    </div>
                    <div className="button-wrapper">
                        <button className="remove">
                            <a className="link" style={{ fontSize: '1.6rem' }} onClick={handlers.openStorefrontModal}>
                                {localized.UpdatePersonaStorefront}
                            </a>
                        </button>
                    </div>
                </div>
            </div>

            {/* Warranty Access */}
            <Authorize as={['Administrator', 'StorefrontAdministrator']}>
                <div>
                    <CheckBox checked={UserHelper.hasRoles(['HasWarrantyAccess'], user)} label="Warranty Claims:" onClick={handlers.openWarrantyModal} />

                    <div className="button-wrapper">
                        <button className="remove">
                            <a className="link" style={{ fontSize: '1.6rem' }} onClick={handlers.openWarrantyModal}>
                                {localized.UpdateWarrantyAccess}
                            </a>
                        </button>
                    </div>
                </div>
            </Authorize>

            {/* Branch Access */}
            {handlers.hasAssignedBranches && (
                <Authorize as={['Administrator', 'StorefrontAdministrator']}>
                    <div style={{ maxWidth: '11vw' }}>
                        <div>
                            <div className="label">{localized.DefaultBranches}</div>
                            <span className="current" style={{ marginLeft: '4px' }}>
                                {branches.available.find(x => x.isDefault)?.number || 'N/A'}
                            </span>
                        </div>
                        <div>
                            <div className="label">{localized.AssignedBranches}</div>
                            <span className="current" style={{ marginLeft: '4px' }}>
                                {branches.assigned.length > 0 ? branches.assigned.join(', ') : 'None'}
                            </span>
                        </div>
                        <div className="button-wrapper">
                            <button className="remove">
                                <a className="link" style={{ fontSize: '1.6rem' }} onClick={handlers.openBranchModal}>
                                    {localized.UpdateAssignedBranches}
                                </a>
                            </button>
                        </div>
                    </div>
                </Authorize>
            )}

            {/* Clone User */}
            <div className="button-wrapper">
                <button className="remove">
                    <a className="link" style={{ fontSize: '1.6rem' }} onClick={handlers.openCloneModal}>
                        Clone User Profile
                    </a>
                </button>
            </div>

            {/* Modals */}

            <MUIModal
                open={modals.storefront}
                onClose={handlers.closeStorefrontModal}               
                title={localized.StorefrontPersona || 'Storefront & Persona'}
                maxWidth="sm"
                customActions={
                    <>
                        <Button
                            type="button"
                            onClick={handlers.closeStorefrontModal}
                            sx={MUIModalButtonStyles}
                        >
                            {localized.Cancel}
                        </Button>
                        <Button
                            type="button"
                            onClick={handlers.saveStorefrontPersona}
                            sx={MUIModalButtonStyles}
                        >
                            {localized.Save}
                        </Button>
                    </>
                }
            >
                <StorefrontPersona
                    updateStorefrontPersona={handlers.setStorefrontPersona}
                    currentStorefront={{ value: storefront.id, label: storefront.displayName }}
                    currentPersona={persona}
                    currentUser={currentUser}
                />
            </MUIModal>
            <MUIModal
                open={modals.warranty}
                onClose={handlers.closeWarrantyModal}
                title={localized.WarrantyAccess || 'Warranty Access'}
                maxWidth="sm"               
                customActions={
                    <>
                        <Button
                            type="button"
                            onClick={handlers.closeWarrantyModal}
                            sx={MUIModalButtonStyles}
                        >
                            {localized.Cancel}
                        </Button>
                        <Button
                            type="button"
                            onClick={handlers.saveWarrantyAccess}
                            sx={MUIModalButtonStyles}
                        >
                            {localized.Save}
                        </Button>
                    </>
                }
            >
                <WarrantyAccess
                    user={user}
                    updateSelectedRoles={handlers.setWarrantyRoles}
                />
            </MUIModal>

            <MUIModal
                open={modals.branches}
                onClose={handlers.cancelBranchModal}
                title={localized.AssignedBranches}
                maxWidth="sm"                
                customActions={
                    <>
                        <Button
                            type="button"
                            onClick={handlers.cancelBranchModal}
                            sx={MUIModalButtonStyles}
                        >
                            {localized.Cancel}
                        </Button>
                        <Button
                            type="button"
                            onClick={handlers.saveBranches}
                            sx={MUIModalButtonStyles}
                        >
                            {localized.Save}
                        </Button>
                    </>
                }
            >
                <Branches
                    header={localized.AssignedBranches}
                    updateBranches={handlers.setModalBranches}
                    user={user}
                    availableBranches={branches.available}
                    modalBranches={branches.modal}
                />
            </MUIModal>

            <MUIModal
                open={modals.clone}
                onClose={handlers.closeCloneModal}
                title={localized.UserToClone || 'User To Clone'}
                maxWidth="sm"               
                customActions={
                    <>
                        <Button
                            type="button"
                            onClick={handlers.closeCloneModal}
                            sx={MUIModalButtonStyles}
                        >
                            {localized.Cancel}
                        </Button>
                        <Button
                            type="button"
                            onClick={handlers.handleCloneSave}
                            sx={MUIModalButtonStyles}
                        >
                            {localized.Apply || 'Apply'}
                        </Button>
                    </>
                }
            >
                {handlers.cloneProcessing ? (
                    <Loading />
                ) : (
                    <Clone
                        isRoleClone={handlers.isRoleClone}
                        setIsRoleClone={handlers.toggleRoleClone}
                        setUsersToClone={handlers.setUsersToClone}
                        user={user}
                        usersToClone={handlers.usersToClone}
                        updateUsersToClone={handlers.updateUsersToClone}
                        CloneProcessing={handlers.cloneProcessing}
                    />
                )}
            </MUIModal>
        </div>
    );
};

UserRoles.propTypes = {
    user: PropTypes.object.isRequired,
    persona: PropTypes.object.isRequired,
    storefront: PropTypes.object.isRequired,
    modals: PropTypes.object.isRequired,
    handlers: PropTypes.object.isRequired,
    localized: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    branches: PropTypes.object.isRequired,
    StorefrontConfig: PropTypes.object.isRequired
};

export default UserRoles;
